import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, ButtonBase, IconButton, InputAdornment, Menu, MenuItem, Popover, Stack, TextField, Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { visuallyHidden } from '@mui/utils';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';

const LOCALE = 'en-CA';
const STEP = 15; // minutes between the times offered
const DAY = 24 * 60;
const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const LABELS = {
  choose: 'Pick a date and time', previousMonth: 'Previous month', nextMonth: 'Next month', time: 'Time',
  zone: 'Time zone', done: 'Done',
};
const ARROWS = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };

const pad = (number) => String(number).padStart(2, '0');

/** "2026-11-09T12:00" (a datetime-local value, the person's own time) as a Date, or null. */
const parse = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(String(value || ''));
  if (!match) return null;
  const [year, month, day, hours, minutes] = match.slice(1).map(Number);
  return new Date(year, month - 1, day, hours, minutes);
};
const toValue = (date) => (
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
);
const dayKey = (date) => toValue(date).slice(0, 10);
const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);
const addMonths = (month, count) => new Date(month.getFullYear(), month.getMonth() + count, 1);
const addDays = (date, count) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + count);
const minutesOf = (date) => date.getHours() * 60 + date.getMinutes();
const at = (day, minutes) => new Date(day.getFullYear(), day.getMonth(), day.getDate(), Math.floor(minutes / 60), minutes % 60);

const timeText = (minutes) => at(new Date(2000, 0, 1), minutes).toLocaleTimeString(LOCALE, { hour: 'numeric', minute: '2-digit' });
const fieldText = (date) => date.toLocaleString(LOCALE, {
  weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  timeZoneName: 'short',
});
const dayName = (date) => date.toLocaleDateString(LOCALE, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

/**
 * The time zone the times are in (this browser's, as every time on the page) on a date, its summer or winter name:
 * "Eastern Standard Time (Toronto)".
 */
const zoneText = (date) => {
  const name = new Intl.DateTimeFormat(LOCALE, { timeZoneName: 'long' }).formatToParts(date)
    .find((part) => part.type === 'timeZoneName')?.value || '';
  const place = (Intl.DateTimeFormat().resolvedOptions().timeZone || '').split('/').pop().replace(/_/g, ' ');
  return place && place !== name ? `${name} (${place})` : name;
};

/** The month's days, Sunday first, with the ends of the months around it to fill its weeks. */
const daysOf = (month) => {
  const first = startOfMonth(month);
  const count = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
  const start = addDays(first, -first.getDay());
  return Array.from({ length: Math.ceil((first.getDay() + count) / 7) * 7 }, (_, index) => addDays(start, index));
};

/** The first time left on `day` after `min`, in minutes on a whole step: DAY when none is left, 0 without `min`. */
const earliestOn = (day, min) => {
  if (!min) return 0;
  const key = dayKey(day);
  if (key < dayKey(min)) return DAY;
  if (key > dayKey(min)) return 0;
  const passed = (min.getTime() - new Date(min.getFullYear(), min.getMonth(), min.getDate()).getTime()) / 60000;
  return Math.ceil(passed / STEP) * STEP;
};

/** The first moment that can be picked from `from` on. */
const firstFree = (from, min) => {
  const minutes = earliestOn(from, min);
  return minutes < DAY ? at(from, minutes) : at(addDays(from, 1), 0);
};

/**
 * A date and time picked on a calendar, the time of day under it and the time zone it is in, kept with Done; its
 * field reads "Mon, Nov 9, 2026, 12:00 p.m. EST". `value` and `onChange(value)` are datetime-local values
 * ("2026-11-09T12:00", the person's own time); days and times before `min` (a Date) can't be picked. Times come every
 * 15 minutes. `labels` names its parts (LABELS); everything else goes to the field (TextField: label, helperText,
 * error, required, sx, ...).
 */
const DateTimeField = ({ value, onChange, min = null, labels: named, ...field }) => {
  const theme = useTheme();
  const { slate } = theme.palette.modern;
  const labels = { ...LABELS, ...named };
  const chosen = parse(value);
  const boxRef = useRef(null);
  const gridRef = useRef(null);
  const focusDay = useRef(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(null);
  const [month, setMonth] = useState(() => startOfMonth(chosen || new Date()));
  const [timeAnchor, setTimeAnchor] = useState(null);

  const times = useMemo(() => {
    const list = Array.from({ length: DAY / STEP }, (_, index) => index * STEP);
    const current = draft ? minutesOf(draft) : null;
    return current !== null && current % STEP ? [...list, current].sort((a, b) => a - b) : list;
  }, [draft]);

  const show = () => {
    const start = chosen && (!min || chosen >= min) ? chosen : firstFree(min || new Date(), min);
    setDraft(start);
    setMonth(startOfMonth(start));
    focusDay.current = dayKey(start);
    setOpen(true);
  };

  // The chosen day takes the focus when the calendar opens, and the day the arrows reach when the month turns.
  useEffect(() => {
    if (!open || !focusDay.current) return undefined;
    const frame = window.requestAnimationFrame(() => {
      gridRef.current?.querySelector(`[data-day="${focusDay.current}"]`)?.focus();
      focusDay.current = null;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [open, month]);

  const pick = (day) => {
    const minutes = Math.max(draft ? minutesOf(draft) : 0, earliestOn(day, min));
    setDraft(at(day, Math.min(minutes, DAY - STEP)));
  };

  const move = (event, day) => {
    const step = ARROWS[event.key];
    if (!step) return;
    event.preventDefault();
    const next = addDays(day, step);
    if (next.getMonth() !== month.getMonth()) {
      focusDay.current = dayKey(next);
      setMonth(startOfMonth(next));
    } else {
      gridRef.current?.querySelector(`[data-day="${dayKey(next)}"]`)?.focus();
    }
  };

  const done = () => {
    setOpen(false);
    if (draft) onChange(toValue(draft));
  };

  const today = new Date();
  const days = daysOf(month);
  const inMonth = (day) => day.getMonth() === month.getMonth();
  const free = (day) => inMonth(day) && earliestOn(day, min) < DAY;
  // One day in the grid takes the Tab key (the chosen one, else the first that can be picked); the arrows move on.
  const tabDay = draft && inMonth(draft) && free(draft) ? dayKey(draft) : dayKey(days.find(free) || month);
  const monthText = month.toLocaleDateString(LOCALE, { month: 'long', year: 'numeric' });
  const round = { width: 36, height: 36, backgroundColor: alpha(slate[500], 0.1), color: slate[800], '&:hover': { backgroundColor: alpha(slate[500], 0.18) } };

  return (
    <>
      <TextField
        {...field}
        value={chosen ? fieldText(chosen) : ''}
        onClick={show}
        onKeyDown={(event) => {
          if (['Enter', ' ', 'ArrowDown'].includes(event.key)) {
            event.preventDefault();
            show();
          }
        }}
        InputProps={{
          ref: boxRef,
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <CalendarMonthOutlinedIcon fontSize="small" sx={{ color: slate[600] }} />
            </InputAdornment>
          ),
          sx: { cursor: 'pointer', '& input': { cursor: 'pointer' } },
        }}
        inputProps={{ 'aria-haspopup': 'dialog', 'aria-expanded': open }}
        InputLabelProps={{ shrink: true }}
      />
      <Popover
        open={open}
        anchorEl={boxRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { role: 'dialog', 'aria-label': labels.choose, sx: { mt: 1, p: 2.5, width: 340, maxWidth: 'calc(100vw - 32px)' } } }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <IconButton
            aria-label={labels.previousMonth}
            onClick={() => setMonth(addMonths(month, -1))}
            disabled={Boolean(min) && month <= startOfMonth(min)}
            sx={round}
          >
            <ChevronLeftRoundedIcon />
          </IconButton>
          <Typography aria-live="polite" sx={{ fontWeight: 600, color: slate[900] }}>{monthText}</Typography>
          <IconButton aria-label={labels.nextMonth} onClick={() => setMonth(addMonths(month, 1))} sx={round}>
            <ChevronRightRoundedIcon />
          </IconButton>
        </Stack>

        <Box
          ref={gridRef}
          role="group"
          aria-label={monthText}
          sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 40px)', justifyContent: 'space-between', rowGap: 0.75 }}
        >
          {WEEKDAYS.map((weekday) => (
            <Typography key={weekday} aria-hidden="true" variant="body2" sx={{ textAlign: 'center', color: slate[600], pb: 0.5 }}>
              {weekday}
            </Typography>
          ))}
          {days.map((day) => {
            const key = dayKey(day);
            const cell = {
              width: 40, height: 40, borderRadius: '50%', display: 'grid', placeItems: 'center', fontSize: '0.9375rem',
              fontVariantNumeric: 'tabular-nums',
            };
            if (!inMonth(day)) {
              return <Box key={key} aria-hidden="true" sx={{ ...cell, color: slate[300] }}>{day.getDate()}</Box>;
            }
            const selected = Boolean(draft) && key === dayKey(draft);
            return (
              <ButtonBase
                key={key}
                data-day={key}
                disabled={!free(day)}
                tabIndex={key === tabDay ? 0 : -1}
                aria-label={dayName(day)}
                aria-pressed={selected}
                onClick={() => pick(day)}
                onKeyDown={(event) => move(event, day)}
                sx={{
                  ...cell,
                  color: slate[900],
                  transition: `background-color ${theme.modern.motion.standard}`,
                  '&:hover': { backgroundColor: alpha(slate[500], 0.12) },
                  '&.Mui-focusVisible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 1 },
                  '&.Mui-disabled': { color: slate[300] },
                  ...(key === dayKey(today) && !selected ? { boxShadow: `inset 0 0 0 1px ${slate[300]}` } : {}),
                  ...(selected ? {
                    backgroundColor: slate[900], color: theme.palette.common.white, fontWeight: 700,
                    '&:hover': { backgroundColor: slate[900] },
                  } : {}),
                }}
              >
                {day.getDate()}
              </ButtonBase>
            );
          })}
        </Box>

        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 2, color: slate[600] }}>
          <PublicRoundedIcon aria-hidden="true" sx={{ fontSize: 18 }} />
          <Typography variant="body2" sx={{ color: 'inherit' }}>
            <Box component="span" sx={visuallyHidden}>{`${labels.zone}: `}</Box>
            {zoneText(draft || new Date())}
          </Typography>
        </Stack>

        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5} sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            color="inherit"
            disabled={!draft}
            onClick={(event) => setTimeAnchor(event.currentTarget)}
            aria-haspopup="menu"
            aria-expanded={Boolean(timeAnchor)}
            aria-label={draft ? `${labels.time}, ${timeText(minutesOf(draft))}` : labels.time}
            startIcon={<AccessTimeRoundedIcon />}
            endIcon={<KeyboardArrowDownRoundedIcon />}
            sx={{ borderRadius: 999, borderColor: slate[300], color: slate[900], fontWeight: 500, px: 1.75 }}
          >
            {draft ? timeText(minutesOf(draft)) : labels.time}
          </Button>
          <Button variant="dark" onClick={done} disabled={!draft}>{labels.done}</Button>
        </Stack>

        <Menu
          anchorEl={timeAnchor}
          open={Boolean(timeAnchor)}
          onClose={() => setTimeAnchor(null)}
          MenuListProps={{ 'aria-label': labels.time, dense: true }}
          slotProps={{ paper: { sx: { maxHeight: 280 } } }}
        >
          {draft && times.map((minutes) => (
            <MenuItem
              key={minutes}
              selected={minutes === minutesOf(draft)}
              disabled={minutes < earliestOn(draft, min)}
              onClick={() => {
                setDraft(at(draft, minutes));
                setTimeAnchor(null);
              }}
            >
              {timeText(minutes)}
            </MenuItem>
          ))}
        </Menu>
      </Popover>
    </>
  );
};

export default DateTimeField;
